
import { useState, useEffect } from "react";

export interface Panel {
  id: number;
  x: number;
  y: number;
  width: number;
  height: number;
  imageIndex: number;
}

// This hook helps detect panels in webtoon images
export const usePanelDetection = (images: string[]) => {
  const [panels, setPanels] = useState<Panel[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // For now, we'll use a simplified approach with manually defined panels
  // In a production app, you might want to use ML/CV to detect panels automatically
  useEffect(() => {
    if (!images || images.length === 0) {
      setPanels([]);
      setIsLoading(false);
      return;
    }

    const detectPanels = async () => {
      setIsLoading(true);
      
      // Create simulated panels for each image
      // In a real implementation, this would analyze the images to find actual panels
      const detectedPanels: Panel[] = [];
      let panelId = 0;

      // For each image, create sample panels
      images.forEach((image, imageIndex) => {
        // Create a temporary image element to get dimensions
        const img = new Image();
        img.src = image;
        
        img.onload = () => {
          const imageWidth = img.width;
          const imageHeight = img.height;
          
          // For demonstration, divide each image into 2-3 panels vertically
          const panelsPerImage = Math.max(2, Math.floor(imageHeight / (imageWidth * 0.6)));
          const panelHeight = imageHeight / panelsPerImage;
          
          for (let i = 0; i < panelsPerImage; i++) {
            detectedPanels.push({
              id: panelId++,
              x: 0,
              y: i * panelHeight,
              width: imageWidth,
              height: panelHeight,
              imageIndex
            });
          }
          
          // If this was the last image, set the panels and mark loading as complete
          if (imageIndex === images.length - 1) {
            setPanels(detectedPanels);
            setIsLoading(false);
          }
        };
      });
    };

    detectPanels();
  }, [images]);

  return { panels, isLoading };
};
